/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { ContractAR, Bills } from "./FinanceCenter";
import CreditCards from "./finance/CreditCards";
import Receipts from "./finance/Receipts";
import { Landmark, FileText, Receipt, Coins, Loader2, ExternalLink, CreditCard, HandCoins } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const ST = {
  pending: { zh: "待兑现", en: "Pending", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  due_today: { zh: "今日到期", en: "Due today", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  overdue: { zh: "已逾期", en: "Overdue", cls: "bg-red-100 text-red-700 border-red-200" },
  fulfilled: { zh: "已兑现", en: "Fulfilled", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

function PaymentPromises({ zh }) {
  const { api } = useApp();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [weekly, setWeekly] = useState(null);
  useEffect(() => { api.get("/finance/payment-promises").then(({ data }) => { setItems(data.items || []); setStats(data.stats || null); }).catch(() => setItems([])); }, []); // eslint-disable-line
  useEffect(() => { api.get("/finance/promise-weekly").then(({ data }) => setWeekly(data.weeks || [])).catch(() => setWeekly([])); }, []); // eslint-disable-line
  if (!items) return <div className="text-sm text-slate-400 py-10 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-1" />{zh ? "加载付款承诺…" : "Loading…"}</div>;
  const order = ["overdue", "due_today", "pending", "fulfilled"];
  const rows = order.flatMap((g) => items.filter((x) => x.status === g));
  const cnt = (s) => items.filter((x) => x.status === s).length;
  return (
    <div className="space-y-3" data-testid="payment-promises-board">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="promise-stats">
          <div className="bg-white rounded-xl border border-slate-200 p-3">
            <p className="text-[11px] text-slate-400">{zh ? "承诺兑现率" : "Fulfill rate"}</p>
            <p className="text-xl font-bold text-slate-800" data-testid="promise-fulfill-rate">{stats.fulfillRate == null ? "—" : `${stats.fulfillRate}%`}</p>
            <p className="text-[10px] text-slate-400">{zh ? `已兑现 ${stats.fulfilled} / 已决 ${stats.decided}` : `${stats.fulfilled}/${stats.decided} decided`}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-3">
            <p className="text-[11px] text-slate-400">{zh ? "平均拖延天数" : "Avg delay"}</p>
            <p className={`text-xl font-bold ${(stats.avgDelayDays || 0) > 7 ? "text-red-600" : "text-slate-800"}`} data-testid="promise-avg-delay">{stats.avgDelayDays == null ? "—" : `${stats.avgDelayDays} ${zh ? "天" : "d"}`}</p>
            <p className="text-[10px] text-slate-400">{zh ? "兑现延迟+逾期天数均值" : "late + overdue days"}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-3 md:col-span-2">
            <p className="text-[11px] text-slate-400">{zh ? "惯性拖款客户（≥2次迟付/逾期）" : "Habitual late payers (≥2)"}</p>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="promise-habitual">
              {(stats.habitualLate || []).length === 0 && <span className="text-sm text-slate-300">{zh ? "暂无" : "None"}</span>}
              {(stats.habitualLate || []).map((h, i) => (
                <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200" data-testid={`promise-habitual-${i}`}>{h.who} ×{h.lateCount}</span>
              ))}
            </div>
          </div>
        </div>
      )}
      {weekly && weekly.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="promise-weekly-chart">
          <h3 className="font-semibold text-slate-900 text-sm mb-2">{zh ? "近8周 履约率与回款趋势" : "8-Week Fulfillment & Collection Trend"}
            <span className="ml-2 text-[11px] font-normal text-slate-400">{zh ? "柱=当周回款金额 · 线=承诺履约率%（无已决承诺的周不计）" : "Bars = weekly collections · Line = fulfill rate %"}</span>
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={weekly} margin={{ top: 6, right: 6, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis yAxisId="l" tickFormatter={(v) => "$" + (v >= 1000 ? Math.round(v / 1000) + "k" : v)} tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tickFormatter={(v) => v + "%"} tick={{ fontSize: 11, fill: "#d97706" }} />
              <Tooltip formatter={(v, name) => (String(name).includes("%") || String(name).includes("履约") ? (v == null ? "—" : v + "%") : "$" + Number(v || 0).toLocaleString())} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="l" dataKey="collected" name={zh ? "回款金额" : "Collected"} fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Line yAxisId="r" type="monotone" dataKey="fulfillRate" name={zh ? "履约率%" : "Fulfill %"} stroke="#d97706" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="flex flex-wrap gap-2 text-[11px]">
        {order.map((s) => (
          <span key={s} className={`px-2 py-1 rounded-full border ${ST[s].cls}`} data-testid={`promise-count-${s}`}>{zh ? ST[s].zh : ST[s].en} · {cnt(s)}</span>
        ))}
        <span className="text-slate-400 self-center">{zh ? "（客户短信付款承诺，AI 自动识别）" : "(AI-detected from customer SMS)"}</span>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <ResponsiveTable className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
              <th className="px-3 py-2 whitespace-nowrap">{zh ? "承诺付款日" : "Promised date"}</th>
              <th className="px-3 py-2">{zh ? "客户 / 项目" : "Customer / Project"}</th>
              <th className="px-3 py-2">{zh ? "金额" : "Amount"}</th>
              <th className="px-3 py-2">{zh ? "摘要 / 原文" : "Summary"}</th>
              <th className="px-3 py-2">{zh ? "状态" : "Status"}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => (
              <tr key={x.smsId} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`promise-row-${x.smsId}`}>
                <td className="px-3 py-2 font-medium text-slate-700 whitespace-nowrap">{x.date || (zh ? "未明确" : "—")}</td>
                <td className="px-3 py-2 text-slate-700 max-w-[220px] truncate" title={x.who}>{x.who}</td>
                <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{x.amount ? `$${Number(x.amount).toLocaleString()}` : "—"}</td>
                <td className="px-3 py-2 text-slate-500 max-w-[340px]"><p className="truncate" title={x.body}>{x.summary || x.body}</p></td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ST[x.status]?.cls}`}>{zh ? ST[x.status]?.zh : ST[x.status]?.en}</span>
                  {x.escalated && <span className="ml-1 text-[10px] text-red-500 font-medium">{zh ? "已升级催款" : "escalated"}</span>}
                </td>
                <td className="px-3 py-2">
                  {x.link && <button data-testid={`promise-open-${x.smsId}`} onClick={() => nav(x.link)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] text-indigo-600 hover:underline inline-flex items-center gap-0.5"><ExternalLink className="h-3 w-3" />{zh ? "查看" : "Open"}</button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-300 text-sm">{zh ? "暂无付款承诺记录" : "No promises yet"}</td></tr>}
          </tbody>
        </ResponsiveTable>
      </div>
    </div>
  );
}

export default function FundsManagement() {
  const { lang, user, featVis } = useApp();
  const zh = lang === "zh";
  const canAR = ["admin", "finance", "ceo"].includes(user.role);
  const [tab, setTab] = useState(canAR ? "ar" : "ap");
  const [sp] = useSearchParams();
  useEffect(() => { const t = sp.get("tab"); if ((["ar", "promises", "cards", "receipts"].includes(t) && canAR) || t === "ap") setTab(t); }, [sp, canAR]);
  const TABS = [
    { k: "ar", label: zh ? "应收 AR · 合同应收/开票" : "Receivables (AR)", icon: FileText, show: canAR },
    { k: "ap", label: zh ? "应付 AP · 供应商账单" : "Payables (AP)", icon: Receipt, show: true },
    { k: "promises", label: zh ? "付款承诺" : "Payment Promises", icon: Coins, show: canAR },
    { k: "cards", label: zh ? "信用卡对账" : "Credit Cards", icon: CreditCard, show: canAR },
    { k: "receipts", label: zh ? "收款流水 (QB)" : "Receipts (QB)", icon: HandCoins, show: canAR },
  ].filter((t) => featVis(`/finance/funds::${t.k}`, t.show));
  const tabKeys = TABS.map((x) => x.k).join(",");
  useEffect(() => { if (TABS.length && !TABS.some((x) => x.k === tab)) setTab(TABS[0].k); }, [tabKeys, tab]); // eslint-disable-line
  return (
    <div className="space-y-5" data-testid="funds-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Landmark className="h-6 w-6 text-red-600" />{zh ? "资金管理" : "Funds Management"}</h1>
        <p className="text-slate-500 text-sm">{zh ? "应收 AR（合同开票/回款/催收）、应付 AP（供应商账单）与客户付款承诺统一管理" : "Unified AR, AP and customer payment promises"}</p>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-slate-200" data-testid="funds-tabs">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`funds-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.k ? "border-brand-500 text-brand-500" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <t.icon className="h-4 w-4" />{t.label}
          </button>
        ))}
      </div>
      {tab === "ar" && TABS.some((x) => x.k === "ar") && <ContractAR zh={zh} />}
      {tab === "ap" && TABS.some((x) => x.k === "ap") && <Bills zh={zh} />}
      {tab === "promises" && TABS.some((x) => x.k === "promises") && <PaymentPromises zh={zh} />}
      {tab === "cards" && TABS.some((x) => x.k === "cards") && <CreditCards zh={zh} />}
      {tab === "receipts" && TABS.some((x) => x.k === "receipts") && <Receipts zh={zh} />}
    </div>
  );
}
